/**
 * usePortalWebSocket - WebSocket hook for portal real-time notifications
 * Deployed to: frontend/src/hooks/usePortalWebSocket.js
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECT_DELAY = 30000;
const WS_MAX_RETRIES = 5;

export default function usePortalWebSocket(onNotification) {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectDelay = useRef(WS_RECONNECT_DELAY);
  const retryCount = useRef(0);
  const [connected, setConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const connect = useCallback(() => {
    const token = localStorage.getItem('client_portal_token');
    if (!token) return;

    // Determine WS URL
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Token is sent as the first frame, never in the URL (credential-in-URL incident #3, 2026-09-07)
    const wsUrl = `${protocol}//${window.location.host}/ws/portal`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'auth', token }));
        setConnected(true);
        reconnectDelay.current = WS_RECONNECT_DELAY;
        retryCount.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'connected') {
            if (data.unread_count !== undefined) {
              setUnreadCount(data.unread_count);
            }
          } else if (data.type === 'portal_notification') {
            setUnreadCount(prev => prev + 1);
            if (onNotification) {
              onNotification(data.notification);
            }
          } else if (data.type === 'unread_count') {
            setUnreadCount(data.count);
          }
        } catch (e) {
          // Ignore non-JSON messages (like "pong")
        }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        // Auto-reconnect with exponential backoff (max retries to prevent storms)
        if (retryCount.current < WS_MAX_RETRIES) {
          retryCount.current += 1;
          reconnectTimer.current = setTimeout(() => {
            reconnectDelay.current = Math.min(reconnectDelay.current * 1.5, WS_MAX_RECONNECT_DELAY);
            connect();
          }, reconnectDelay.current);
        }
      };

      ws.onerror = () => {
        ws.close();
      };

      // Keepalive ping every 30s
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      ws._pingInterval = pingInterval;
    } catch (e) {
      // Reconnect on failure
      reconnectTimer.current = setTimeout(connect, reconnectDelay.current);
    }
  }, [onNotification]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (wsRef.current) {
      if (wsRef.current._pingInterval) {
        clearInterval(wsRef.current._pingInterval);
      }
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  return { connected, unreadCount, setUnreadCount, disconnect, reconnect: connect };
}
